/**
 * Hand-rolled history router.
 *
 * The Workers Static Assets binding is configured with
 * `not_found_handling = "single-page-application"`, so any path the bundle
 * doesn't have a literal asset for returns `index.html` — meaning we can use
 * real paths (e.g. `/pay/:token`) without hash fragments. Externally generated
 * URLs (payment links, OAuth redirects) land directly on the right SPA route.
 *
 * `useRoute()` returns the current path (pathname + search) and re-renders on
 * `popstate` OR on programmatic `navigate()`. `navigate()` pushes a new entry
 * and notifies subscribers manually since `pushState` doesn't fire `popstate`.
 */
import { useEffect, useState } from 'hono/jsx';

function read(): string {
  return window.location.pathname + window.location.search;
}

const listeners = new Set<() => void>();
function notify(): void { listeners.forEach((l) => l()); }

export function useRoute(): string {
  const [path, setPath] = useState<string>(read());
  useEffect(() => {
    const onChange = () => setPath(read());
    window.addEventListener('popstate', onChange);
    listeners.add(onChange);
    return () => {
      window.removeEventListener('popstate', onChange);
      listeners.delete(onChange);
    };
  }, []);
  return path;
}

export function navigate(path: string): void {
  if (read() === path) return;
  window.history.pushState({}, '', path);
  notify();
}

/** Like `navigate` but doesn't push a new entry — for sync-from-state updates
 * (search box, pagination) that shouldn't fill up browser history. */
export function replacePath(path: string): void {
  if (read() === path) return;
  window.history.replaceState({}, '', path);
  notify();
}

/**
 * Match a path against `/foo/:id/bar`-style patterns and return param values,
 * or `null` if no match. Strips the query string before matching.
 */
export function matchRoute(path: string, pattern: string): Record<string, string> | null {
  const pParts = path.split('?')[0].split('/').filter(Boolean);
  const tParts = pattern.split('/').filter(Boolean);
  if (pParts.length !== tParts.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < tParts.length; i++) {
    const t = tParts[i];
    if (t.startsWith(':')) params[t.slice(1)] = decodeURIComponent(pParts[i]);
    else if (t !== pParts[i]) return null;
  }
  return params;
}

/** Parse the query string portion of a path (anything after `?`). */
export function parseQuery(path: string): URLSearchParams {
  const i = path.indexOf('?');
  return new URLSearchParams(i < 0 ? '' : path.slice(i + 1));
}
